import { normalizeManagerName } from './managerStats';

function bool(value) {
  return value === true || String(value).toLowerCase() === 'true';
}

function seasonYear(row) {
  return Number.parseInt(row.year ?? row.Year ?? row.Season, 10) || 0;
}

function madePlayoffs(row) {
  return bool(row.playoffsMade ?? row['Playoffs Made']);
}

function playoffStreak(rows) {
  const seasons = rows.slice().sort((first, second) => seasonYear(second) - seasonYear(first));
  if (!seasons.length || seasonYear(seasons[0]) !== 2025) return 0;
  let streak = 0;
  for (const row of seasons) {
    if (!madePlayoffs(row)) break;
    streak += 1;
  }
  return streak;
}

function nextTen(value) {
  return Math.ceil((value + 1) / 10) * 10;
}

export function buildManagerMilestone(manager, rankings = [], seasonRows = []) {
  const rankedByPoints = rankings.slice().sort((first, second) => second.stats.total_points - first.stats.total_points);
  const entry = rankings.find((row) => row.manager === manager);
  if (!entry) return null;
  const index = rankedByPoints.findIndex((row) => row.manager === manager);
  const ahead = index > 0 ? rankedByPoints[index - 1] : null;
  const behind = index >= 0 && index < rankedByPoints.length - 1 ? rankedByPoints[index + 1] : null;
  const wins = entry.stats.total_wins;
  const winTarget = nextTen(wins);
  const championshipYears = entry.stats.championship_years || [];
  const latestTitle = championshipYears.length ? Math.max(...championshipYears) : null;
  const titleLeader = Math.max(...rankings.map((row) => row.stats.championships));
  const streak = playoffStreak(seasonRows);

  return {
    manager,
    pointsRank: index + 1,
    points: entry.stats.total_points,
    winTarget,
    winsNeeded: winTarget - wins,
    winCopy: `${winTarget - wins} ${winTarget - wins === 1 ? 'win' : 'wins'} to reach ${winTarget}.`,
    pointsTarget: ahead ? ahead.manager : null,
    pointsNeeded: ahead ? Math.round(((ahead.stats.total_points - entry.stats.total_points) + 0.01) * 100) / 100 : null,
    pointsCopy: ahead
      ? `${Math.round(((ahead.stats.total_points - entry.stats.total_points) + 0.01) * 100) / 100} points to pass ${ahead.manager} for #${index} all-time.`
      : `${Math.round((entry.stats.total_points - behind.stats.total_points) * 100) / 100} points ahead of ${behind.manager} atop the lifetime table.`,
    playoffStreak: streak,
    playoffCopy: streak ? `${streak} straight playoff ${streak === 1 ? 'appearance' : 'appearances'} entering 2026.` : 'A new playoff streak starts with this season.',
    latestTitle,
    droughtCopy: latestTitle === 2025
      ? 'Defending champion. The repeat is live.'
      : latestTitle
        ? `${2025 - latestTitle} completed seasons since the ${latestTitle} title.`
        : `Still hunting a first ring after ${entry.stats.seasons_played} seasons.`,
    titlesToLeader: titleLeader - entry.stats.championships,
    titleCopy: entry.stats.championships === titleLeader
      ? `Owns or shares the all-time mark with ${titleLeader} titles.`
      : `${titleLeader - entry.stats.championships} ${titleLeader - entry.stats.championships === 1 ? 'title' : 'titles'} behind the all-time mark.`,
  };
}

export function buildRecordsUnderAttack(rankings = [], standingsRows = [], activeManagers = []) {
  return activeManagers
    .map((manager) => buildManagerMilestone(
      manager,
      rankings,
      standingsRows.filter((row) => normalizeManagerName(row.Manager ?? row.manager) === manager),
    ))
    .filter(Boolean)
    .sort((first, second) => first.winsNeeded - second.winsNeeded || first.pointsRank - second.pointsRank);
}
